// src/components/TransportationModal.js
import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CloseButton,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";
import { Link } from "react-router-dom";
import TimePicker from "./TimePicker";
import ProviderTypeahead from "./ProviderTypeahead";
import NavGuardModal from "./NavGuardModal";
import AutoGrowTextarea from "./AutoGrowTextarea";

const RIDER_CATEGORY_OPTIONS = ["MAN", "WOMAN", "COUPLE"];

function driverIsEligible(driverType, riderCategory) {
  if (!riderCategory) return true;
  if (driverType === "COUPLE") return true;
  if (driverType === "MALE") return riderCategory === "MAN" || riderCategory === "COUPLE";
  if (driverType === "FEMALE") return riderCategory === "WOMAN" || riderCategory === "COUPLE";
  return false;
}

const STATUS_OPTIONS = [
  "REQUESTED",
  "ASSIGNED",
  "CONFIRMED",
  "COMPLETED",
  "CANCELLED",
  "NO_SHOW",
];

const STATUS_COLORS = {
  REQUESTED: "warning",
  ASSIGNED:  "primary",
  CONFIRMED: "success",
  COMPLETED: "secondary",
  CANCELLED: "danger",
  NO_SHOW:   "dark",
};

function toDateInput(dt) {
  return dt ? String(dt).slice(0, 10) : "";
}
function toTimeInput(dt) {
  const s = String(dt || "");
  return s.length >= 16 ? s.slice(11, 16) : "";
}
function guessRiderCategory(client) {
  // Base each request on the client's permanent rider category; fall back to a
  // spouse-based guess when one hasn't been set yet.
  return client?.rider_category || (client?.spouse_name?.trim() ? "COUPLE" : "");
}
function clientAddress(client) {
  return [
    client?.address_line1,
    client?.address_line2,
    client?.city,
    client?.state,
    client?.postal_code,
  ]
    .filter(Boolean)
    .join(", ");
}

const emptyForm = {
  pickup_date: "",
  pickup_time: "08:00",
  dropoff_location_text: "",
  pickup_location_text: "",
  provider_id: "",
  rider_category: "",
  driver_id: "",
  appointment_confirmed: false,
  driver_confirmed: false,
  status: "REQUESTED",
  no_show_party: "",
  notes: "",
};

const emptyNewProvider = { name: "", phone: "", city: "" };

export default function TransportationModal({
  show,
  onHide,
  onSaved,
  clientId,
  clientData,
  editRide,
  context = "dashboard",
}) {
  const isEdit = !!editRide;
  // Drivers are assigned only on the Transportation Dashboard. When this modal
  // is opened from a client's Service Request, hide all driver-assignment UI.
  const isServiceRequest = context === "service-request";

  // Lookups (self-fetched)
  const [allDrivers, setAllDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  // Main form
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Client-level driver note: lives on the client, not this ride, so it shows
  // on every ride for them. Saved separately, only when it changed.
  const [driverNote, setDriverNote] = useState("");
  const [initialDriverNote, setInitialDriverNote] = useState("");

  // "Close without saving?" guard, triggered by the top-right X.
  const [closeGuard, setCloseGuard] = useState(false);

  const { user } = useContext(AuthContext);
  const isAdminOrDirector = !!(
    user?.roles?.includes("ADMIN") || user?.roles?.includes("DIRECTOR")
  );

  const handleDelete = async () => {
    if (!editRide?.id) return;
    if (!window.confirm(
      "Delete this transportation item? This removes the ride, its driver " +
      "assignments, and the linked service request, and cannot be undone."
    )) return;
    setSaving(true);
    setError("");
    try {
      await api.delete(`/drivers/rides/${editRide.id}`);
      onSaved();
      onHide();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to delete transportation item.");
      setSaving(false);
    }
  };


  // Inline new-provider sub-form
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [newProvider, setNewProvider] = useState(emptyNewProvider);
  const [addingProvider, setAddingProvider] = useState(false);
  const [providerError, setProviderError] = useState("");

  // Load lookups + populate form whenever modal opens
  useEffect(() => {
    if (!show) return;

    setError("");
    setShowAddProvider(false);
    setNewProvider(emptyNewProvider);
    setProviderError("");
    setCloseGuard(false);

    // Fetch drivers + providers in parallel
    const loadLookups = async () => {
      setLoadingLookups(true);
      try {
        const [dRes, pRes] = await Promise.all([
          api.get("/drivers", { params: { active: "true" } }),
          api.get("/drivers/providers"),
        ]);
        const loadedDrivers = dRes.data.items || [];
        setAllDrivers(loadedDrivers);
        setFilteredDrivers(loadedDrivers);
        setProviders(pRes.data.items || []);
      } catch (_) {
        // non-critical; form still works
      } finally {
        setLoadingLookups(false);
      }
    };
    loadLookups();

    const note = (isEdit ? editRide?.client_driver_notes : clientData?.driver_notes) || "";
    setDriverNote(note);
    setInitialDriverNote(note);

    // Populate form fields
    if (isEdit && editRide) {
      const primary = (editRide.assignments || []).find(
        (a) => a.role === "PRIMARY" && ["ASSIGNED", "CONFIRMED"].includes(a.status)
      );
      setForm({
        pickup_date: toDateInput(editRide.pickup_datetime),
        pickup_time: toTimeInput(editRide.pickup_datetime),
        dropoff_location_text: editRide.dropoff_location_text || "",
        pickup_location_text: editRide.pickup_location_text || "",
        provider_id: editRide.provider_id ? String(editRide.provider_id) : "",
        rider_category: editRide.rider_category || "",
        driver_id: primary?.driver_id ? String(primary.driver_id) : "",
        appointment_confirmed: !!editRide.appointment_confirmed,
        driver_confirmed: !!editRide.driver_confirmed,
        status: editRide.status || "REQUESTED",
        no_show_party: editRide.no_show_party || "",
        notes: editRide.notes || "",
      });
    } else {
      setForm({
        ...emptyForm,
        rider_category: guessRiderCategory(clientData),
        pickup_location_text: clientAddress(clientData),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Reactively filter eligible drivers when rider category, date, or time changes
  useEffect(() => {
    const { rider_category, pickup_date, pickup_time } = form;

    if (!rider_category) {
      setFilteredDrivers(allDrivers);
      return;
    }

    if (!pickup_date || !pickup_time) {
      setFilteredDrivers(allDrivers.filter((d) => driverIsEligible(d.driver_type, rider_category)));
      return;
    }

    let active = true;
    api
      .get("/drivers/eligible", {
        params: {
          rider_category,
          pickup_datetime: `${pickup_date}T${pickup_time}:00`,
          ...(editRide?.id ? { exclude_ride_id: editRide.id } : {}),
        },
      })
      .then((res) => {
        if (active) setFilteredDrivers(res.data.items || []);
      })
      .catch(() => {
        if (active)
          setFilteredDrivers(allDrivers.filter((d) => driverIsEligible(d.driver_type, rider_category)));
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.rider_category, form.pickup_date, form.pickup_time, allDrivers]);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const setNP = (field, value) => setNewProvider((p) => ({ ...p, [field]: value }));

  // ── Inline provider save ──────────────────────────────────
  const handleAddProvider = async () => {
    if (!newProvider.name.trim()) {
      setProviderError("Provider name is required.");
      return;
    }
    setProviderError("");
    setAddingProvider(true);
    try {
      const res = await api.post("/drivers/providers", {
        name: newProvider.name.trim(),
        phone: newProvider.phone || null,
        city: newProvider.city || null,
      });
      const created = res.data;
      setProviders((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name))
      );
      set("provider_id", String(created.id));
      setNewProvider(emptyNewProvider);
      setShowAddProvider(false);
    } catch (e) {
      // 409 means it already exists — the API returns the existing record
      if (e?.response?.status === 409 && e.response.data?.provider) {
        const existing = e.response.data.provider;
        setProviders((prev) => {
          if (prev.find((p) => p.id === existing.id)) return prev;
          return [...prev, existing].sort((a, b) => a.name.localeCompare(b.name));
        });
        set("provider_id", String(existing.id));
        setNewProvider(emptyNewProvider);
        setShowAddProvider(false);
      } else {
        setProviderError(e?.response?.data?.error || "Failed to save provider.");
      }
    } finally {
      setAddingProvider(false);
    }
  };

  // ── Main save ─────────────────────────────────────────────
  const validate = () => {
    if (!form.pickup_date) return "Appointment date is required.";
    if (!form.pickup_time) return "Appointment time is required.";
    if (!form.rider_category) return "Rider category is required.";
    if (form.status === "NO_SHOW" && !form.no_show_party)
      return "For a No-Show, select who didn't show: the client or the driver.";
    return "";
  };

  const handleSave = async () => {
    const msg = validate();
    if (msg) { setError(msg); return; }
    setError("");
    setSaving(true);

    const pickupDatetime = `${form.pickup_date}T${form.pickup_time}:00`;

    try {
      // Driver note first: if it fails nothing has been saved yet, so a retry
      // can't create a duplicate ride.
      const noteChanged = driverNote.trim() !== initialDriverNote.trim();
      if (noteChanged) {
        const noteClientId = editRide?.client_id ?? clientId;
        await api.put(`/drivers/clients/${noteClientId}/driver-note`, {
          driver_notes: driverNote,
        });
        setInitialDriverNote(driverNote);
      }

      let savedRide;

      if (isEdit) {
        const payload = {
          pickup_datetime: pickupDatetime,
          dropoff_location_text: form.dropoff_location_text || null,
          pickup_location_text: form.pickup_location_text || null,
          provider_id: form.provider_id ? Number(form.provider_id) : null,
          appointment_confirmed: form.appointment_confirmed,
          notes: form.notes || null,
        };
        // The Service Request view never touches driver assignment or the ride
        // status — those are managed on the Transportation Dashboard.
        if (!isServiceRequest) {
          payload.driver_confirmed = form.driver_confirmed;
          payload.status = form.status;
          payload.no_show_party = form.status === "NO_SHOW" ? form.no_show_party : null;
        }
        const res = await api.patch(`/drivers/rides/${editRide.id}`, payload);
        savedRide = res.data;

        // Reassign driver if changed (dashboard only).
        if (!isServiceRequest) {
          const primary = (editRide.assignments || []).find(
            (a) => a.role === "PRIMARY" && ["ASSIGNED", "CONFIRMED"].includes(a.status)
          );
          const oldDriverId = primary?.driver_id ? String(primary.driver_id) : "";
          if (form.driver_id !== oldDriverId) {
            if (primary) {
              await api.delete(`/drivers/assignments/${primary.id}`);
            }
            if (form.driver_id) {
              await api.post(`/drivers/rides/${editRide.id}/assign`, {
                driver_id: Number(form.driver_id),
                auto_confirm: form.driver_confirmed,
              });
            }
          }
        }
      } else {
        const payload = {
          client_id: clientId,
          rider_category: form.rider_category,
          pickup_datetime: pickupDatetime,
          dropoff_location_text: form.dropoff_location_text || null,
          pickup_location_text: form.pickup_location_text || null,
          provider_id: form.provider_id ? Number(form.provider_id) : null,
          appointment_confirmed: form.appointment_confirmed,
          driver_confirmed: form.driver_confirmed,
          notes: form.notes || null,
        };
        const res = await api.post("/drivers/rides", payload);
        savedRide = res.data;

        if (form.driver_id) {
          await api.post(`/drivers/rides/${savedRide.id}/assign`, {
            driver_id: Number(form.driver_id),
            auto_confirm: form.driver_confirmed,
          });
        }
      }

      onSaved(savedRide, noteChanged ? { driver_notes: driverNote.trim() || null } : undefined);
      onHide();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to save transportation request.");
    } finally {
      setSaving(false);
    }
  };

  // ── Selected provider lookup ──────────────────────────────
  const selectedProvider = form.provider_id
    ? providers.find((p) => String(p.id) === form.provider_id)
    : null;

  // ── Client contact info (shown at the top of the modal) ───
  // When editing, the ride carries the client fields; when creating from a
  // Service Request, they come from clientData.
  const clientName = isEdit
    ? editRide?.client_name
    : clientData
    ? `${clientData.first_name || ""} ${clientData.last_name || ""}`.trim()
    : "";
  const clientPhone = isEdit
    ? editRide?.client_phone
    : clientData?.mobile_phone || clientData?.home_phone;
  const clientAddr = isEdit ? editRide?.client_address : clientAddress(clientData);
  const emergencyName = isEdit
    ? editRide?.client_emergency_contact_name
    : clientData?.emergency_contact_name;
  const emergencyPhone = isEdit
    ? editRide?.client_emergency_contact_phone
    : clientData?.emergency_contact_phone;

  // Top-right "X" close guard: offer Save & Close / Close Without Saving.
  const guardOnSave = async () => {
    await handleSave();
    // handleSave closes the modal on success; on a validation error it leaves
    // the form open with the message shown, so just dismiss the guard.
    setCloseGuard(false);
  };
  const guardOnDiscard = () => {
    setCloseGuard(false);
    onHide();
  };

  return (
    <>
    <Modal show={show} onHide={onHide} centered size="lg" enforceFocus={!closeGuard}>
      <Modal.Header className="d-block">
        <div className="d-flex align-items-center">
        <Modal.Title className="h5 mb-0">
          {isEdit ? (
            <>
              Edit Transportation Request{" "}
              {editRide?.status && (
                <Badge bg={STATUS_COLORS[editRide.status] || "secondary"}>
                  {editRide.status}
                </Badge>
              )}
            </>
          ) : (
            "New Transportation Request"
          )}
        </Modal.Title>
        <div className="ms-auto d-flex align-items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving && <Spinner size="sm" animation="border" className="me-1" />}
            {isEdit ? "Save Changes" : "Create Request"}
          </Button>
          <CloseButton aria-label="Close" onClick={() => setCloseGuard(true)} />
        </div>
        </div>
        {/* Client contact info */}
        {(clientName || clientPhone || clientAddr) && (
          <div className="small text-muted d-flex flex-wrap align-items-center gap-3 mt-2">
            <span className="fw-semibold text-body">{clientName || "—"}</span>
            {clientPhone ? (
              <span><i className="bi bi-telephone me-1" />{clientPhone}</span>
            ) : null}
            {clientAddr ? (
              <span><i className="bi bi-geo-alt me-1" />{clientAddr}</span>
            ) : null}
            {emergencyName ? (
              <span>
                <i className="bi bi-exclamation-circle me-1" />
                {emergencyName}
                {emergencyPhone ? ` · ${emergencyPhone}` : ""}
              </span>
            ) : null}
          </div>
        )}
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form>
          {/* Date + Time */}
          <Row className="g-2 mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Appointment Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={form.pickup_date}
                  onChange={(e) => set("pickup_date", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Appointment Time <span className="text-danger">*</span>
                </Form.Label>
                <TimePicker
                  value={form.pickup_time}
                  onChange={(v) => set("pickup_time", v)}
                  minuteStep={5}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Locations */}
          <Form.Group className="mb-3">
            <Form.Label>Appointment Location (Destination)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Clinic / facility name and address"
              value={form.dropoff_location_text}
              onChange={(e) => set("dropoff_location_text", e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Pickup Location</Form.Label>
            <Form.Control
              type="text"
              placeholder="Client's address"
              value={form.pickup_location_text}
              onChange={(e) => set("pickup_location_text", e.target.value)}
            />
          </Form.Group>

          {/* Provider */}
          <Form.Group className="mb-1">
            <Form.Label>Provider</Form.Label>
            <ProviderTypeahead
              providers={providers}
              value={form.provider_id}
              onChange={(id) => set("provider_id", id)}
              disabled={loadingLookups}
            />
            {selectedProvider?.phone && (
              <Form.Text className="text-muted">
                Phone: {selectedProvider.phone}
                {selectedProvider.address
                  ? ` · ${selectedProvider.address}${selectedProvider.city ? `, ${selectedProvider.city}` : ""}`
                  : ""}
              </Form.Text>
            )}
          </Form.Group>

          <div className="mb-3">
            <Button
              size="sm"
              variant="link"
              className="p-0 text-decoration-none"
              onClick={() => {
                setShowAddProvider((v) => !v);
                setProviderError("");
              }}
            >
              {showAddProvider ? "— Cancel new provider" : "+ Add New Provider"}
            </Button>
          </div>

          {showAddProvider && (
            <Card className="mb-3 border-secondary">
              <Card.Header
                className="py-1 px-3"
                style={{ fontSize: "0.85rem", background: "#f8f9fa" }}
              >
                New Provider
              </Card.Header>
              <Card.Body className="py-2 px-3">
                {providerError && (
                  <Alert variant="danger" className="py-1 mb-2">
                    {providerError}
                  </Alert>
                )}
                <Row className="g-2 align-items-end">
                  <Col md={5}>
                    <Form.Control
                      size="sm"
                      placeholder="Name *"
                      value={newProvider.name}
                      onChange={(e) => setNP("name", e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddProvider()}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Control
                      size="sm"
                      type="tel"
                      placeholder="Phone"
                      value={newProvider.phone}
                      onChange={(e) => setNP("phone", e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Control
                      size="sm"
                      placeholder="City"
                      value={newProvider.city}
                      onChange={(e) => setNP("city", e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Button
                      size="sm"
                      variant="success"
                      className="w-100"
                      onClick={handleAddProvider}
                      disabled={addingProvider}
                    >
                      {addingProvider ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Rider + Driver */}
          <Row className="g-2 mb-3">
            <Col md={isServiceRequest ? 12 : 6}>
              <Form.Group>
                <Form.Label>
                  Rider Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={form.rider_category}
                  onChange={(e) => set("rider_category", e.target.value)}
                >
                  <option value="">— Select —</option>
                  {RIDER_CATEGORY_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  {isServiceRequest
                    ? "Drivers are assigned on the Transportation Dashboard."
                    : "Used to match eligible drivers."}
                </Form.Text>
              </Form.Group>
            </Col>
            {!isServiceRequest && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Assigned Driver</Form.Label>
                  <Form.Select
                    value={form.driver_id}
                    onChange={(e) => {
                      const driverId = e.target.value;
                      setForm((p) => {
                        const driverConfirmed = !!driverId;
                        let status = p.status;
                        if (driverConfirmed && status === "CONFIRMED") status = "ASSIGNED";
                        else if (!driverConfirmed && status === "ASSIGNED") status = "CONFIRMED";
                        return { ...p, driver_id: driverId, driver_confirmed: driverConfirmed, status };
                      });
                    }}
                    disabled={loadingLookups || !form.appointment_confirmed}
                  >
                    <option value="">
                      {form.appointment_confirmed
                        ? "— Unassigned —"
                        : "Appointment must be Verified first!"}
                    </option>
                    {form.appointment_confirmed &&
                      filteredDrivers.map((d) => (
                        <option key={d.id} value={String(d.id)}>
                          {d.display_name} ({d.driver_type})
                        </option>
                      ))}
                  </Form.Select>
                  {form.rider_category && form.appointment_confirmed && (
                    <Form.Text className="text-muted">
                      {form.pickup_date && form.pickup_time
                        ? "Showing drivers eligible and available for this date/time."
                        : "Showing eligible drivers. Add date & time to check availability."}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            )}
          </Row>

          {/* Driver coordination (outreach + assignment) all happens on the
              Transportation Dashboard. */}
          <Row className="g-2 mb-3">
            <Col className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Text className="text-muted mb-0">
                All transportation requests are handled on the Transportation
                Dashboard.
              </Form.Text>
              <Button
                as={Link}
                to="/transportation"
                variant="outline-primary"
                size="sm"
                onClick={onHide}
              >
                Go to Transportation Dashboard
              </Button>
            </Col>
          </Row>

          {/* Confirmation status — read-only. Appointment is verified via the
              dashboard Verify button; driver confirmation follows assignment. */}
          <Row className="g-2 mb-3">
            <Col>
              <span className="me-2">Appointment Verified</span>
              {form.appointment_confirmed && (
                <i className="bi bi-check-circle-fill text-success" title="Verified" />
              )}
            </Col>
            {!isServiceRequest && (
              <Col>
                <span className="me-2">Driver Confirmed</span>
                {form.driver_confirmed && (
                  <i className="bi bi-check-circle-fill text-success" title="Confirmed" />
                )}
              </Col>
            )}
          </Row>

          {/* Ride status (dashboard edit only — the Service Request uses the
              unified status set on the client's Services list). */}
          {isEdit && !isServiceRequest && (
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>

              {/* No-Show requires recording who failed to show. */}
              {form.status === "NO_SHOW" && (
                <div className="mt-2">
                  <Form.Label className="mb-1">Who didn&apos;t show?</Form.Label>
                  <Form.Select
                    value={form.no_show_party}
                    onChange={(e) => set("no_show_party", e.target.value)}
                  >
                    <option value="">Select…</option>
                    <option value="CLIENT">Client (not home / didn&apos;t answer)</option>
                    <option value="DRIVER">Driver</option>
                  </Form.Select>
                  {form.no_show_party === "CLIENT" && (
                    <Form.Text className="text-muted">
                      Service will be set to <strong>Cancelled</strong> with note
                      “Client didn't answer”.
                    </Form.Text>
                  )}
                  {form.no_show_party === "DRIVER" && (
                    <Form.Text className="text-muted">
                      Service will be set to <strong>Cancelled</strong> with note
                      “Driver didn't show”, and a follow-up task will be created for
                      an intake worker to call the driver and client and reschedule.
                    </Form.Text>
                  )}
                </div>
              )}
            </Form.Group>
          )}

          {/* Notes */}
          <Form.Group>
            <Form.Label>Notes</Form.Label>
            <AutoGrowTextarea
              rows={2}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </Form.Group>

          {/* Driver note (client-level) */}
          <Form.Group className="mt-3">
            <Form.Label>Driver note (applies to all rides for this client)</Form.Label>
            <AutoGrowTextarea
              rows={2}
              value={driverNote}
              onChange={(e) => setDriverNote(e.target.value)}
              placeholder="e.g., Dog in yard, use side door"
            />
            <Form.Text muted>
              Saved on the client, so drivers see it on every ride. Drivers can edit it too.
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        {isEdit && isAdminOrDirector && (
          <Button
            variant="outline-danger"
            className="me-auto"
            onClick={handleDelete}
            disabled={saving}
          >
            <i className="bi bi-trash me-1" />Delete
          </Button>
        )}
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving && <Spinner size="sm" animation="border" className="me-1" />}
          {isEdit ? "Save Changes" : "Create Request"}
        </Button>
      </Modal.Footer>

    </Modal>

    <NavGuardModal
      show={closeGuard}
      saving={saving}
      error={error}
      onSave={guardOnSave}
      onDiscard={guardOnDiscard}
      onCancel={() => setCloseGuard(false)}
      title="Close without saving?"
      body="Would you like to save your changes before closing?"
      saveLabel="Save & Close"
      discardLabel="Close Without Saving"
    />
    </>
  );
}
